import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
// Type-only — no runtime cycle (useFetchSettings does not import payoutMethods).
import type { Settings } from './useFetchSettings'

export type MethodId = 'crypto-solana-usdc' | 'paypal' | 'stripe-connect' | 'tremendous'

// i18n strings are lingui MessageDescriptors built with `msg`` (extracted at module load) and
// resolved at the render site with `t(descriptor)` — see WithdrawModal. A dynamic `t(string)` is
// never extracted, which is why these must be descriptors and not raw strings (FU1).
export type FieldDef = {
  key: string // RHF field name AND methodInput key, e.g. 'walletAddress' | 'recipientEmail'
  label: MessageDescriptor
  placeholder: MessageDescriptor
  validate: (value: string) => string | true // returns true | a LOCKED_CODES key on failure
  serverField: string // wire path backend reports field errors under
}

export type PayoutMethodDef = {
  id: MethodId // == backend `method` string
  displayName: MessageDescriptor
  shortName: string // brand short name, e.g. 'Solana' — plain string, not localized like displayName
  minAmountUsd: number
  // The `Settings` key holding this method's runtime min; omitted ⇒ inherit global `minPayoutUsd` (IV4).
  minSettingKey?: 'cryptoMinPayoutUsd' | 'paypalMinPayoutUsd' | 'tremendousMinPayoutUsd'
  decimalPrecision: number
  requiresConfirmation: boolean
  fields: FieldDef[]
  staticMethodInput?: Record<string, string>
  // Client-side cap for this method's amount, when one exists; omitted ⇒ no method-specific max.
  maxAmountUsd?: number
  confirmCopy: (vals: Record<string, string>, amountUsd: string) => MessageDescriptor
  inFlight?: 'crypto-stepbar'
  // OAuth-consent capability (PayPal): when the settings selector says 'oauth', the withdraw
  // flow redirects to the provider's consent screen instead of collecting `fields`. The
  // email-mode `fields` stay defined — 'email' settings keep today's flow byte-identical (IV4).
  oauthRedirect?: true
  oauthConfirmCopy?: (amountUsd: string) => MessageDescriptor
}

export const PAYOUT_METHODS: Record<MethodId, PayoutMethodDef> = {
  'crypto-solana-usdc': {
    id: 'crypto-solana-usdc',
    displayName: msg`USDC on Solana`,
    shortName: 'Solana',
    minAmountUsd: 0.01,
    minSettingKey: 'cryptoMinPayoutUsd',
    decimalPrecision: 2,
    requiresConfirmation: true,
    fields: [
      {
        key: 'walletAddress',
        // Keep this string byte-for-byte: it's the existing msgid, so reusing it relights the
        // translations rather than minting a new untranslated id.
        label: msg`Destination wallet address (Solana)`,
        placeholder: msg`Destination wallet address (Solana)`,
        // Solana base58 pubkey: 32-44 chars, no 0/O/I/l
        validate: (v) => (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v) ? true : 'INVALID_SOLANA_ADDRESS'),
        serverField: 'methodInput.walletAddress'
      }
    ],
    confirmCopy: (v, amt) => {
      const walletAddress = v.walletAddress
      return msg`Send $${amt} to ${walletAddress} on Solana?`
    },
    inFlight: 'crypto-stepbar'
  },
  paypal: {
    id: 'paypal',
    displayName: msg`USD via PayPal`,
    shortName: 'PayPal',
    minAmountUsd: 0.01,
    minSettingKey: 'paypalMinPayoutUsd',
    decimalPrecision: 2,
    requiresConfirmation: true,
    fields: [
      {
        key: 'recipientEmail',
        label: msg`PayPal email`,
        placeholder: msg`you@example.com`,
        validate: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? true : 'INVALID_EMAIL'),
        serverField: 'methodInput.recipientEmail'
      }
    ],
    staticMethodInput: { currency: 'USD' },
    confirmCopy: (v, amt) => {
      const recipientEmail = v.recipientEmail
      return msg`Send $${amt} to ${recipientEmail} via PayPal?`
    },
    oauthRedirect: true,
    oauthConfirmCopy: (amt) => msg`Send $${amt} via PayPal? You'll confirm the destination account on PayPal.`
  },
  'stripe-connect': {
    id: 'stripe-connect',
    displayName: msg`USD via Stripe`,
    shortName: 'Stripe',
    minAmountUsd: 0.01,
    decimalPrecision: 2,
    requiresConfirmation: true,
    fields: [],
    staticMethodInput: { currency: 'usd' },
    confirmCopy: (_v, amt) => msg`Send $${amt} via Stripe to your connected account?`
  },
  tremendous: {
    id: 'tremendous',
    displayName: msg`USD via Tremendous`,
    shortName: 'Tremendous',
    minAmountUsd: 10,
    minSettingKey: 'tremendousMinPayoutUsd',
    decimalPrecision: 2,
    requiresConfirmation: true,
    fields: [],
    staticMethodInput: {},
    // The tile's max now comes from GET /v1/user/payout-methods (`maxPayoutUsd`) when the endpoint
    // reports one. `maxAmountUsd: 2000` below is only the fallback for when that field is absent —
    // source of truth for the figure itself: TREMENDOUS_MAX_AMOUNT_USD in nectar-api's
    // methods/tremendous/method.ts. It will drift silently if the server-side cap ever changes
    // without this fallback being updated too.
    maxAmountUsd: 2000,
    confirmCopy: (_v, amt) => msg`Send $${amt} via Tremendous?`
  }
}

// Announced-only picker entries — NOT submittable, never sent as `method`
export const ANNOUNCED_METHODS: readonly { value: string; label: MessageDescriptor }[] = []

// Picker options = functional (enabled) + announced (disabled/"coming soon").
// `label` is a MessageDescriptor — resolve with `t(label)` at the render site (WithdrawModal).
export const PAYOUT_METHOD_OPTIONS = [
  ...Object.values(PAYOUT_METHODS).map((m) => ({ value: m.id, label: m.displayName, comingSoon: false })),
  ...ANNOUNCED_METHODS.map((a) => ({ value: a.value, label: a.label, comingSoon: true }))
]

// "Enabled" means switched on and not paused.
export const isMethodEnabled = (settings: Settings | undefined, methodId: MethodId): boolean => {
  if (methodId === 'paypal') return !settings?.paypalPayoutsPaused
  if (methodId === 'stripe-connect') return settings?.stripeConnectEnabled === true && !settings.stripePayoutsPaused
  if (methodId === 'tremendous') return settings?.tremendousEnabled === true && !settings.tremendousPayoutsPaused
  return true
}

export const getEnabledPayoutMethodOptions = (settings: Settings | undefined) =>
  PAYOUT_METHOD_OPTIONS.filter((option) => {
    if (option.comingSoon) return true
    return isMethodEnabled(settings, option.value as MethodId)
  })

export const getEnabledPayoutMethodDefs = (settings: Settings | undefined): PayoutMethodDef[] =>
  Object.values(PAYOUT_METHODS).filter((def) => isMethodEnabled(settings, def.id))

// Resolve a method's runtime min: per-method setting override → global setting → def fallback (IV1, IV3, IV4, PC1).
export const resolveMethodMin = (settings: Settings | undefined, def: PayoutMethodDef | undefined): number =>
  (def?.minSettingKey ? settings?.[def.minSettingKey] : undefined) ??
  settings?.minPayoutUsd ??
  def?.minAmountUsd ??
  PAYOUT_METHODS['crypto-solana-usdc'].minAmountUsd

// Lowest min across all functional methods — for an overall threshold (e.g. BalanceWidget) (IV1).
export const resolveLowestMin = (settings: Settings | undefined): number =>
  Math.min(...getEnabledPayoutMethodDefs(settings).map((def) => resolveMethodMin(settings, def)))
